import { UserProfile } from '../types';

/**
 * Extract mentioned user IDs from text by matching @FullName against available users.
 * 
 * Uses exact full-name matching to prevent partial matches.
 * Example: "@Michael Ehlers" matches only Michael, not Ronny Ehlers or Mareike Ehlers.
 * 
 * Users are sorted by name length descending so longer names match first,
 * preventing "Max Mustermann" from being consumed by a user named "Max".
 */
export const extractMentionedIds = (text: string, users: UserProfile[]): string[] => {
    const ids = new Set<string>();
    
    // Sort by full name length descending — longest names match first
    const sortedUsers = [...users].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.length;
        const nameB = `${b.first_name} ${b.last_name}`.length;
        return nameB - nameA;
    });

    let remaining = text;
    for (const user of sortedUsers) {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        if (!fullName || fullName.length < 2) continue;
        
        // Escape special regex characters in name
        const escaped = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match @FullName followed by word boundary or end of string (case-insensitive)
        const regex = new RegExp(`@${escaped}(?:\\b|$)`, 'gi');
        
        if (regex.test(remaining)) {
            ids.add(user.id);
            // Remove matched mention to prevent a shorter name from re-matching the same text
            remaining = remaining.replace(regex, '');
        }
    }
    
    return Array.from(ids);
};
